<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import AiBadgePill from './AiBadgePill.vue';
import type { IssueListItemDto } from '@/stores/issues';
import {
  SEVERITY_LABEL, SEVERITY_BADGE, SEVERITY_BORDER_COLOR,
  STATUS_LABEL, STATUS_BADGE,
  SOURCE_LABEL,
} from './issue-enums';

dayjs.extend(relativeTime);

interface Props {
  issue: IssueListItemDto;
}
const props = defineProps<Props>();

const severityVariant = computed(() => SEVERITY_BADGE[props.issue.severity] ?? 'info');
const statusVariant = computed(() => STATUS_BADGE[props.issue.status] ?? 'info');
const borderColor = computed(() => SEVERITY_BORDER_COLOR[props.issue.severity] ?? 'bg-muted');
const aiChanged = computed(
  () =>
    props.issue.aiClassified &&
    props.issue.aiSeverity !== null &&
    props.issue.aiSeverity !== undefined &&
    props.issue.aiSeverity !== props.issue.severity,
);
</script>

<template>
  <div
    class="flex gap-3 bg-surface border border-border rounded-lg overflow-hidden"
    data-testid="issue-mobile-card"
  >
    <!-- Left severity stripe: w-1 rounded-sm in severity color (per spec §2.2.6) -->
    <div :class="['w-1 flex-shrink-0 rounded-sm', borderColor]" aria-hidden="true" />

    <div class="flex-1 min-w-0 py-3 pr-3">
      <div class="flex items-start justify-between gap-2">
        <div class="flex items-center gap-1.5 flex-wrap">
          <BaseBadge :variant="severityVariant">
            {{ SEVERITY_LABEL[issue.severity] ?? String(issue.severity) }}
          </BaseBadge>
          <BaseBadge :variant="statusVariant" :no-dot="true">
            {{ STATUS_LABEL[issue.status] ?? String(issue.status) }}
          </BaseBadge>
          <AiBadgePill v-if="aiChanged" label="AI" />
        </div>
        <span class="text-caption text-muted font-mono tabular-nums flex-shrink-0">
          {{ dayjs(issue.updatedAt).fromNow() }}
        </span>
      </div>

      <p class="text-body text-fg font-medium mt-1.5 line-clamp-2" data-testid="issue-title">
        {{ issue.title }}
      </p>
      <p v-if="issue.summary" class="text-caption text-muted mt-0.5 line-clamp-2">{{ issue.summary }}</p>

      <div class="flex flex-wrap gap-2 mt-1.5 text-caption text-muted">
        <span v-if="issue.location">{{ issue.location }}</span>
        <span>{{ SOURCE_LABEL[issue.source] ?? String(issue.source) }}</span>
        <span v-if="issue.isRepeat" class="text-orange-600 font-medium">
          Repeat ({{ issue.repeatCount }}×)
        </span>
        <span v-if="issue.aiConfidence !== null && issue.aiConfidence !== undefined" class="text-teal-700 font-mono tabular-nums">
          AI: {{ issue.aiConfidence }}%
        </span>
      </div>
    </div>
  </div>
</template>
